import { motion } from "framer-motion";
import { useRoute } from "wouter";
import { useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Calendar, Clock, Building2, Users, TrendingUp } from "lucide-react";
import { getCompanyBySlug, formatDuration, formatDateRange } from "@/lib/companyUtils";
import SkillCategoriesDisplay from "@/components/skills/components/SkillCategoriesDisplay";
import Activities from "@/components/Activities";
import NotFound from "./not-found";

// Function to get photo height based on image aspect ratio for fixed width of 800px
const getPhotoHeight = (companyId: number) => {
  const fixedWidth = 800; // 800px for company page (larger than timeline popup)
  switch (companyId) {
    case 1: // Mercedes-Benz (2048x1152, ratio 1.78:1)
      return Math.round(fixedWidth / 1.78); // ~449px
    case 2: // FERCHAU (2946x1756, ratio 1.678:1)
      return Math.round(fixedWidth / 1.678); // ~477px
    case 3: // Siemens (1188x690, ratio 1.72:1)
      return Math.round(fixedWidth / 1.72); // ~465px
    case 4: // THB (1090x616, ratio 1.77:1)
      return Math.round(fixedWidth / 1.77); // ~452px
    case 5: // University of Rostock (1402x540, ratio 2.596:1)
      return Math.round(fixedWidth / 2.596); // ~308px
    default:
      return 450; // fallback
  }
};

export default function CompanyPage() {
  const [, params] = useRoute("/professional/:companySlug");
  const companySlug = params?.companySlug;
  
  // Memoize company data lookup to avoid recalculation on re-renders
  const company = useMemo(() => {
    if (!companySlug) return null;
    return getCompanyBySlug(companySlug);
  }, [companySlug]);
  
  // Memoize formatted date range to avoid recalculation
  const dateRange = useMemo(() => {
    if (!company) return '';
    return formatDateRange(company.startDate, company.endDate);
  }, [company]);
  
  // Memoize formatted duration to avoid recalculation
  const formattedDuration = useMemo(() => {
    if (!company) return '';
    return formatDuration(company.totalDuration);
  }, [company]);
  
  if (!companySlug) {
    return <NotFound />;
  }
  
  if (!company) {
    return <NotFound />;
  }
  
  return (
    <main className="container mx-auto p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Header Section */}
        <div className="text-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative inline-block"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-primary/10 to-transparent rounded-lg blur-lg"></div>
            <div className="relative z-10 flex items-center gap-6 px-8 py-6">
              <Avatar className="h-20 w-20">
                <AvatarImage src={company.logoPath} alt={`${company.name} logo`} />
                <AvatarFallback className="text-xl font-bold">
                  {company.logoText}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <h1 className="text-4xl md:text-5xl font-bold">
                  {company.name}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">
                      {dateRange}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">
                      {formattedDuration}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Company Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-3xl mx-auto"
          >
            {company.description}
          </motion.p>
        </div>

        {/* Stats Overview and Company Photo/Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto"
        >
          {/* Left side - KPIs stacked vertically */}
          <div className="space-y-6">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => {
                const activitiesSection = document.getElementById('activities-section');
                activitiesSection?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{company.activities.length}</p>
                    <p className="text-sm text-muted-foreground">
                      {company.activities.length === 1 ? 'Role' : 'Roles'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => {
                const skillsSection = document.getElementById('skills-section');
                skillsSection?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <TrendingUp className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {Object.values(company.skillsByCategory).reduce((total, skills) => total + skills.length, 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Skills Developed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formattedDuration}</p>
                    <p className="text-sm text-muted-foreground">Total Experience</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right side - Company Photo and Map */}
          {(company.photoPath || company.mapsLink) && (
            <div className="lg:col-span-1">
              <Card className="overflow-hidden h-fit">
                {company.photoPath && (
                  <div className="bg-white">
                    <a 
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:opacity-80 transition-opacity"
                    >
                      <img 
                        src={company.photoPath} 
                        alt={`${company.name} photo`}
                        className="w-full cursor-pointer object-cover"
                        style={{ height: `${getPhotoHeight(company.id) * 0.6}px` }}
                      />
                    </a>
                  </div>
                )}
                
                {/* Map section */}
                {company.mapsLink && (
                  <div className={company.photoPath ? "border-t" : ""}>
                    <iframe
                      src={company.mapsLink}
                      width="100%"
                      height="250"
                      style={{ border: 0 }}
                      allowFullScreen={false}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="w-full"
                    />
                  </div>
                )}
              </Card>
            </div>
          )}
        </motion.div>

        {/* Skills Categories Section */}
        <motion.div
          id="skills-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <SkillCategoriesDisplay 
            skillsByCategory={company.skillsByCategory}
            displayMode="progress"
            compactMode={true}
            animated={true}
            calculationType="company"
            activities={company.activities}
          />
        </motion.div>

        {/* Activities Section */}
        <motion.div
          id="activities-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Activities at {company.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Activities companyId={company.id} showHeader={false} />
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </main>
  );
}
